import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { getStatusBadgeClass } from "../config/uiHelpers";

const Campaigns = ({ user, logout }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [sendModal, setSendModal] = useState(null); // { campaign }
  const [recipients, setRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    type: "email",
    target_audience: "",
    content: ""
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/campaigns`, {
        ...newCampaign,
        created_by: user.user_id
      });
      toast.success("Campaign created successfully!");
      setShowCreate(false);
      setNewCampaign({ name: "", type: "email", target_audience: "", content: "" });
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to create campaign");
    }
  };

  const generateAIContent = async () => {
    if (!newCampaign.target_audience) {
      toast.error("Please specify target audience first");
      return;
    }
    
    setGeneratingContent(true);
    try {
      const response = await axios.post(`${API}/ai/generate-content`, {
        prompt: `Create a compelling ${newCampaign.type} campaign for security systems targeting ${newCampaign.target_audience}. Focus on safety, peace of mind, and advanced technology.`,
        content_type: "ad_copy"
      });
      setNewCampaign({...newCampaign, content: response.data.generated_content});
      toast.success("AI content generated!");
    } catch (error) {
      toast.error("AI generation failed");
    } finally {
      setGeneratingContent(false);
    }
  };

  const updateStatus = async (campaignId, status) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/status?status=${status}`);
      toast.success("Campaign status updated!");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openSend = async (campaign) => {
    setSendModal({ campaign });
    setSelectedEmails([]);
    try {
      const r = await axios.get(`${API}/email/recipients`);
      setRecipients(r.data);
    } catch (err) { toast.error("Could not load recipients"); }
  };

  const sendCampaign = async () => {
    if (selectedEmails.length === 0) { toast.error("Select at least one recipient"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/campaigns/${sendModal.campaign.campaign_id}/send`, selectedEmails);
      const data = res.data;
      if (data.provider_key_present) {
        toast.success(`Email sent to ${data.sent} recipient(s)`);
      } else {
        toast.success(`Campaign queued for ${data.sent} recipient(s) — running in test/mock mode. Add RESEND_API_KEY to send real emails.`);
      }
      setSendModal(null);
      fetchCampaigns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const toggleEmail = (email) => {
    setSelectedEmails(s => s.includes(email) ? s.filter(e => e !== email) : [...s, email]);
  };
  const selectAll = () => setSelectedEmails(recipients.map(r => r.email));
  const clearAll = () => setSelectedEmails([]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Campaigns
              </h1>
              <p className="text-base leading-relaxed text-slate-700">
                Create and manage marketing campaigns
              </p>
            </div>
            <button
              data-testid="create-campaign-btn"
              onClick={() => setShowCreate(!showCreate)}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300"
            >
              {showCreate ? 'Cancel' : 'Create Campaign'}
            </button>
          </div>

          {showCreate && (
            <div data-testid="create-campaign-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>New Campaign</h3>
              <form onSubmit={handleCreate} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Campaign Name</label>
                    <input
                      data-testid="campaign-name-input"
                      type="text"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Campaign Type</label>
                    <select
                      data-testid="campaign-type-select"
                      value={newCampaign.type}
                      onChange={(e) => setNewCampaign({...newCampaign, type: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    >
                      <option value="email">Email</option>
                      <option value="social">Social Media</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Target Audience</label>
                  <input
                    data-testid="campaign-audience-input"
                    type="text"
                    value={newCampaign.target_audience}
                    onChange={(e) => setNewCampaign({...newCampaign, target_audience: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    placeholder="e.g., Homeowners, Small businesses, Property managers"
                    required
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500">Campaign Content</label>
                    <button
                      data-testid="generate-ai-content-btn"
                      type="button"
                      onClick={generateAIContent}
                      disabled={generatingContent}
                      className="rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-4 py-2 text-sm font-medium transition-colors duration-300 flex items-center gap-2 disabled:opacity-50"
                    >
                      <Sparkle size={16} weight="fill" />
                      {generatingContent ? 'Generating...' : 'Generate with AI'}
                    </button>
                  </div>
                  <textarea
                    data-testid="campaign-content-input"
                    value={newCampaign.content}
                    onChange={(e) => setNewCampaign({...newCampaign, content: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    rows="6"
                    required
                  />
                </div>
                <button
                  data-testid="submit-campaign-btn"
                  type="submit"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300"
                >
                  Create Campaign
                </button>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div data-testid="campaigns-list" className="space-y-4">
              {campaigns.map((campaign, index) => (
                <div
                  key={campaign.campaign_id}
                  data-testid={`campaign-card-${index}`}
                  className="bg-white border border-slate-200 rounded-none p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>
                          {campaign.name}
                        </h3>
                        <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusBadgeClass(campaign.status, "campaign")}`}>
                          {campaign.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        <strong>Type:</strong> {campaign.type} | <strong>Audience:</strong> {campaign.target_audience}
                      </p>
                      <p className="text-sm text-slate-700 mb-4">{campaign.content}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {campaign.type === "email" && (
                        <button
                          data-testid={`send-campaign-${index}`}
                          onClick={() => openSend(campaign)}
                          className="rounded-none bg-[#25D366] text-white hover:bg-[#128C7E] px-4 py-2 text-sm font-medium transition-colors duration-300"
                        >
                          Send Emails
                        </button>
                      )}
                      {campaign.status === 'draft' && (
                        <button
                          data-testid={`activate-campaign-${index}`}
                          onClick={() => updateStatus(campaign.campaign_id, 'active')}
                          className="rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-4 py-2 text-sm font-medium transition-colors duration-300"
                        >
                          Activate
                        </button>
                      )}
                      {campaign.status === 'active' && (
                        <button
                          data-testid={`complete-campaign-${index}`}
                          onClick={() => updateStatus(campaign.campaign_id, 'completed')}
                          className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-medium transition-colors duration-300"
                        >
                          Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send campaign modal */}
      {sendModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSendModal(null)}>
          <div data-testid="send-modal" className="bg-white border border-slate-200 shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Send Campaign</div>
                <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{sendModal.campaign.name}</h2>
              </div>
              <button onClick={() => setSendModal(null)} className="p-2 hover:bg-slate-100">✕</button>
            </div>
            <div className="p-6 space-y-3 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-bold">{selectedEmails.length}</span> of {recipients.length} selected
                </div>
                <div className="flex gap-2">
                  <button onClick={selectAll} data-testid="select-all-recipients" className="text-xs uppercase tracking-wider font-bold text-[#002FA7] hover:underline">Select all</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={clearAll} className="text-xs uppercase tracking-wider font-bold text-slate-500 hover:underline">Clear</button>
                </div>
              </div>
              <div className="border border-slate-200 max-h-96 overflow-y-auto">
                {recipients.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">No contacts or leads with emails yet.</p>
                ) : recipients.map(r => (
                  <label key={r.email} data-testid={`recipient-${r.email}`} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={selectedEmails.includes(r.email)} onChange={() => toggleEmail(r.email)} />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.email}{r.company ? ` · ${r.company}` : ""}</div>
                    </div>
                    <span className="text-[0.6rem] uppercase tracking-wider font-bold text-slate-400">{r.type}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setSendModal(null)} className="rounded-none border border-slate-300 px-5 py-2.5 hover:bg-slate-50">Cancel</button>
              <button data-testid="confirm-send-btn" onClick={sendCampaign} disabled={sending || selectedEmails.length === 0}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold disabled:opacity-40">
                {sending ? "Sending…" : `Send to ${selectedEmails.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
